/**
 * One shadow-map update per frame on the composer path (H-1145,
 * `q-verdant-reach-over-draw-call-budget`; AD-158 constraint 7).
 *
 * WebGLShadowMap re-renders every caster on EVERY `renderer.render()` while
 * `autoUpdate` is on. The full profile renders the scene twice a frame — the
 * RenderPass, then SSAOPass's normal pass (`_renderOverride` →
 * `renderer.render`) — so the sun's shadow map was drawn twice, and the second
 * copy fed nothing (the normal pass samples no shadows). The fix is the
 * documented three.js idiom: `autoUpdate = false` and arm `needsUpdate` once per
 * frame; the first render consumes it (three resets it to false), later renders
 * in the same frame skip the shadow pass.
 *
 * The direct (degraded) path renders once a frame, so it keeps `autoUpdate`.
 *
 * Pure: imports no `three`, so the policy is testable under `node --test`
 * against a fake that follows three's own skip rule.
 */

/** The two WebGLShadowMap flags this policy drives. */
export interface ShadowMapFlags {
  autoUpdate: boolean;
  needsUpdate: boolean;
}

/** At stack construction: composed ⇒ manual (armed per frame); direct ⇒ automatic. */
export function adoptFrameShadowPolicy(shadowMap: ShadowMapFlags, composed: boolean): void {
  shadowMap.autoUpdate = !composed;
  shadowMap.needsUpdate = false;
}

/** At the top of each composed frame: the frame's first scene render redraws the shadow map. */
export function armFrameShadows(shadowMap: ShadowMapFlags): void {
  if (!shadowMap.autoUpdate) shadowMap.needsUpdate = true;
}
